import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";

import { Condition } from "../types";
import { conditionFormSchema, ConditionVariables } from "../schemas";
import { useCreateConditionMutation, useUpdateConditionMutation } from "../api/mutations";
import { getQueryClient } from "~/providers/query.provider";
import { conditionsKeys } from "../api/queries/keys";

interface UseConditionProps {
  condition?: Condition;
  onClose?: () => void;
  program: 'weight_loss' | 'urgent_care';
}

export const useCondition = ({ condition, onClose, program }: UseConditionProps) => {
  const queryClient = getQueryClient();
  const createConditionMutation = useCreateConditionMutation();
  const updateConditionMutation = useUpdateConditionMutation();

  const form = useForm<ConditionVariables>({
    resolver: zodResolver(conditionFormSchema),
    defaultValues: {
      name: condition?.name ?? "",
      date_diagnosed: condition?.date_diagnosed ?? "",
      is_treated: condition?.is_treated ?? false,
      status: condition?.status ?? "",
      program: program,
    },
  });

  const onSubmit = async (data: ConditionVariables) => {
    try {
      if (condition) {
        updateConditionMutation.mutate({
          id: condition?.condition_id ?? "",
          name: data?.name,
          date_diagnosed: data?.date_diagnosed,
          is_treated: data?.is_treated ?? false,
          status: data?.status ?? "",
          program: program,
        },
          {
            onSuccess: () => {
              toast.success("Condition updated successfully");
              onClose?.();
              queryClient.invalidateQueries({ queryKey: conditionsKeys.conditions(program) });
            },
            onError: () => {
              toast.error("Failed to update condition");
            }
          }
        );
      } else {
        createConditionMutation.mutate({
          name: data?.name,
          date_diagnosed: data?.date_diagnosed,
          is_treated: data?.is_treated ?? false,
          status: data?.status ?? "",
          program: program,
        },
          {
            onSuccess: () => {
              toast.success("Condition added successfully");
              onClose?.();
              queryClient.invalidateQueries({ queryKey: conditionsKeys.conditions(program) });
            },
            onError: () => {
              toast.error("Failed to add condition");
            }
          });
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to update condition");
    }
  };

  return {
    form,
    onSubmit,
    isLoading: createConditionMutation.isPending || updateConditionMutation.isPending,
  };
};
